import { sb } from '../supabase.ts';
import { CF_ACCOUNT_ID, CF_D1_DATABASE_ID, CF_API_TOKEN } from '../config.ts';

// Helper to compute 6-digit student key from mobile number
export function extractStudentKey(mobile: any, fallbackKey?: any): number {
  const str = String(mobile || '').replace(/\D/g, '');
  if (str.length >= 6) {
    return parseInt(str.slice(-6), 10);
  }
  if (fallbackKey && !isNaN(Number(fallbackKey))) {
    return parseInt(String(fallbackKey), 10);
  }
  return 0;
}

// Cloudflare D1 API executor
async function executeD1Query(sql: string, params: any[] = []): Promise<any> {
  const path = `/client/v4/accounts/${CF_ACCOUNT_ID}/d1/database/${CF_D1_DATABASE_ID}/query`;
  let url = `/cf-api${path}`;
  
  try {
    let res = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${CF_API_TOKEN}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ sql, params })
    });
    
    // If /cf-api returns 404 (e.g., XAMPP server without Vite proxy), try direct API
    if (res.status === 404) {
      url = `https://api.cloudflare.com${path}`;
      res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${CF_API_TOKEN}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sql, params })
      });
    }

    const json = await res.json();
    if (!res.ok || !json.success) {
      const msg = json.errors?.[0]?.message || 'D1 API Request failed';
      throw new Error(msg);
    }
    return json.result;
  } catch (err: any) {
    // Fallback for CORS block on direct fetch
    if (err.name === 'TypeError' || err.message?.includes('fetch')) {
      try {
        const corsProxyUrl = `https://corsproxy.io/?${encodeURIComponent('https://api.cloudflare.com' + path)}`;
        const proxyRes = await fetch(corsProxyUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${CF_API_TOKEN}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ sql, params })
        });
        const proxyJson = await proxyRes.json();
        if (proxyRes.ok && proxyJson.success) {
          return proxyJson.result;
        }
      } catch (proxyErr) {
        console.error('CORS Proxy fallback failed:', proxyErr);
      }
    }
    throw err;
  }
}

// Ensure Cloudflare D1 table exists
async function ensureStudentLoginD1Table(): Promise<void> {
  const sql = `CREATE TABLE IF NOT EXISTS student_login (
    iid INTEGER PRIMARY KEY,
    active_class INTEGER,
    active_section TEXT,
    active_roll INTEGER,
    student_key INTEGER
  );`;
  await executeD1Query(sql, []);
}

export async function renderStudentLogInPage(app: HTMLElement): Promise<void> {
  app.innerHTML = `
<style>
  .slog-wrap { background: #f8fafc; min-height: 100vh; padding: 24px 32px; font-family: 'Inter', sans-serif; }
  .slog-header { background: #fff; padding: 24px 32px; border-radius: 16px; border: 1px solid #e2e8f0; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 4px 12px rgba(0,0,0,0.03); }
  .slog-header h2 { margin: 0; font-size: 22px; font-weight: 800; color: #0f172a; display: flex; align-items: center; gap: 10px; }
  .slog-header p { margin: 4px 0 0; font-size: 13px; color: #64748b; }
  
  .slog-controls { background: #fff; padding: 20px 24px; border-radius: 16px; border: 1px solid #e2e8f0; margin-bottom: 20px; display: flex; gap: 16px; flex-wrap: wrap; align-items: flex-end; box-shadow: 0 4px 12px rgba(0,0,0,0.03); }
  .fg { display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 130px; }
  .fg label { font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.05em; }
  .fg select, .fg input { padding: 10px 14px; border: 1px solid #cbd5e1; border-radius: 10px; font-size: 14px; background: #f8fafc; outline: none; }
  
  .btn-action { background: #0ea5e9; color: #fff; border: none; padding: 10px 20px; border-radius: 10px; font-weight: 700; cursor: pointer; transition: background 0.2s, transform 0.2s; font-size: 13px; display: inline-flex; align-items: center; gap: 6px; }
  .btn-action:hover { opacity: 0.9; transform: translateY(-1px); }
  .btn-action:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
  .btn-d1 { background: #f59e0b; }
  .btn-green { background: #10b981 !important; color: #fff; }
  .btn-orange { background: #f59e0b !important; color: #fff; }
  .btn-red { background: #ef4444 !important; color: #fff; }

  .sec-card { background: #fff; border-radius: 16px; border: 1px solid #e2e8f0; margin-bottom: 24px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.03); }
  .sec-header { padding: 16px 24px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e2e8f0; }
  .sec-header-orange { background: #fff7ed; border-bottom-color: #ffedd5; }
  .sec-header-orange h3 { color: #c2410c; }
  .sec-header-green { background: #f0fdf4; border-bottom-color: #dcfce7; }
  .sec-header-green h3 { color: #15803d; }
  .sec-header h3 { margin: 0; font-size: 16px; font-weight: 800; display: flex; align-items: center; gap: 8px; }

  .slog-table { width: 100%; border-collapse: collapse; font-size: 14px; }
  .slog-table th { background: #f1f5f9; padding: 14px 16px; text-align: left; font-size: 11px; font-weight: 800; color: #475569; text-transform: uppercase; border-bottom: 2px solid #e2e8f0; }
  .slog-table td { padding: 12px 16px; border-bottom: 1px solid #e2e8f0; vertical-align: middle; color: #1e293b; }
  
  .val-text { font-weight: 700; font-size: 14px; color: #0f172a; }
  .key-badge { background: #eff6ff; color: #2563eb; padding: 4px 8px; border-radius: 6px; font-weight: 700; font-family: monospace; font-size: 13px; border: 1px solid #bfdbfe; }
  
  .status-bar { padding: 12px 20px; background: #e0f2fe; color: #0369a1; border-radius: 10px; font-size: 13px; font-weight: 600; margin-bottom: 16px; display: none; }

  @keyframes pulseSpin {
    0% { transform: scale(1) rotate(0deg); }
    50% { transform: scale(1.15) rotate(180deg); }
    100% { transform: scale(1) rotate(360deg); }
  }
</style>

<!-- Full-Screen Progress Overlay Modal -->
<div id="syncProgressModal" style="
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
">
  <div style="
    background: #ffffff;
    padding: 40px 48px;
    border-radius: 24px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    text-align: center;
    max-width: 480px;
    width: 90%;
  ">
    <div style="font-size: 52px; margin-bottom: 16px; display: inline-block; animation: pulseSpin 2s infinite cubic-bezier(0.4, 0, 0.2, 1);">⚡</div>
    <h3 style="margin: 0 0 10px; font-size: 22px; font-weight: 800; color: #0f172a;" id="syncModalTitle">Syncing Records</h3>
    <p style="font-size: 16px; font-weight: 700; color: #0284c7; margin: 0 0 24px; min-height: 24px;" id="syncModalText">Starting process...</p>
    
    <div style="width: 100%; background: #e2e8f0; height: 12px; border-radius: 999px; overflow: hidden;">
      <div id="syncProgressBar" style="width: 0%; height: 100%; background: linear-gradient(90deg, #0ea5e9, #f59e0b); transition: width 0.25s ease;"></div>
    </div>
  </div>
</div>

<div class="slog-wrap">
  <div class="slog-header">
    <div>
      <h2>🔑 Student Login ID Sync (Cloudflare D1 & Supabase)</h2>
      <p>Sync class_year, section_year, roll_year & 6-Digit Student Key (from mobile) to Cloudflare D1 <code>student_login</code> table & Supabase. Removes D1 record if session column is empty.</p>
    </div>
    <div style="display: flex; gap: 10px;">
      <a href="/" class="btn-action" style="background: #64748b; text-decoration: none;">← Dashboard</a>
      <button class="btn-action btn-d1" id="syncD1AllBtn">⚡ Bulk Sync Filtered to D1</button>
    </div>
  </div>

  <div id="statusBar" class="status-bar"></div>

  <div class="slog-controls">
    <div class="fg">
      <label>Target Session</label>
      <select id="sessionSel">
        <option value="2026" selected>Session 2026</option>
        <option value="2025">Session 2025</option>
        <option value="2024">Session 2024</option>
      </select>
    </div>
    <div class="fg">
      <label>Class Filter</label>
      <select id="classSel">
        <option value="">All Classes</option>
        <option value="6">Class 6</option>
        <option value="7">Class 7</option>
        <option value="8">Class 8</option>
        <option value="9">Class 9</option>
        <option value="10">Class 10</option>
      </select>
    </div>
    <div class="fg">
      <label>Section Filter</label>
      <select id="secSel">
        <option value="">All Sections</option>
      </select>
    </div>
    <div class="fg" style="flex: 2;">
      <label>Search Student</label>
      <input type="text" id="searchInput" placeholder="Search by name, IID, or mobile...">
    </div>
    <button class="btn-action" id="loadDataBtn">🔍 Load Students</button>
  </div>

  <div id="sectionsContainer">
    <div style="padding: 40px; text-align: center; color: #64748b; background: #fff; border-radius: 16px; border: 1px solid #e2e8f0;">
      Click "Load Students" to fetch student data from database.
    </div>
  </div>
</div>
  `;

  const sessionSel = document.getElementById('sessionSel') as HTMLSelectElement;
  const classSel = document.getElementById('classSel') as HTMLSelectElement;
  const secSel = document.getElementById('secSel') as HTMLSelectElement;
  const searchInput = document.getElementById('searchInput') as HTMLInputElement;
  const loadDataBtn = document.getElementById('loadDataBtn') as HTMLButtonElement;
  const syncD1AllBtn = document.getElementById('syncD1AllBtn') as HTMLButtonElement;
  const sectionsContainer = document.getElementById('sectionsContainer')!;
  const statusBar = document.getElementById('statusBar')!;

  const syncProgressModal = document.getElementById('syncProgressModal')!;
  const syncModalTitle = document.getElementById('syncModalTitle')!;
  const syncModalText = document.getElementById('syncModalText')!;
  const syncProgressBar = document.getElementById('syncProgressBar')!;

  function showModalProgress(title: string, text: string, percent: number) {
    syncProgressModal.style.opacity = '1';
    syncProgressModal.style.pointerEvents = 'auto';
    syncModalTitle.textContent = title;
    syncModalText.textContent = text;
    syncProgressBar.style.width = `${Math.min(100, Math.max(0, percent))}%`;
  }

  function hideModalProgress() {
    syncProgressModal.style.opacity = '0';
    syncProgressModal.style.pointerEvents = 'none';
  }

  let studentsData: any[] = [];

  function showStatus(msg: string, isError = false) {
    statusBar.style.display = 'block';
    statusBar.style.background = isError ? '#fef2f2' : '#e0f2fe';
    statusBar.style.color = isError ? '#991b1b' : '#0369a1';
    statusBar.textContent = msg;
  }

  async function loadStudents() {
    sectionsContainer.innerHTML = '<div style="padding: 40px; text-align: center; color: #64748b; background: #fff; border-radius: 16px;">Fetching student records...</div>';
    
    const session = sessionSel.value || '2026';
    const classCol = `class_${session}`;
    const sectionCol = `section_${session}`;
    const rollCol = `roll_${session}`;

    try {
      let query = sb
        .from('student_database')
        .select('iid, student_name_en, active_class, active_section, active_roll, father_mobile, mother_mobile, guardian_mobile, student_key, class_2026, section_2026, roll_2026, class_2025, section_2025, roll_2025, class_2024, section_2024, roll_2024, status, student_final_status');

      if (classSel.value) {
        query = query.or(`${classCol}.eq.${classSel.value},active_class.eq.${classSel.value}`);
      } else {
        query = query.or(`${classCol}.not.is.null,active_class.not.is.null`);
      }

      const { data, error } = await query.order('iid', { ascending: true });

      if (error) throw error;

      studentsData = (data || []).map((s: any) => {
        const mobile = s.father_mobile || s.mother_mobile || s.guardian_mobile || '';
        const computedKey = extractStudentKey(mobile, s.student_key);

        const hasSessionData = s[classCol] !== null && s[classCol] !== undefined && Number(s[classCol]) > 0;
        
        const targetClass = hasSessionData ? Number(s[classCol]) : 0;
        const targetSection = hasSessionData ? (s[sectionCol] || 'A') : '';
        const targetRoll = hasSessionData ? Number(s[rollCol] || 0) : 0;

        // Needs to be cleared/deleted from D1 if session column has NO data but active_class HAS data
        const needsDeleteFromD1 = !hasSessionData && (s.active_class !== null && Number(s.active_class) > 0);

        // Supabase student_final_status becomes false if status contains TC, or was already false
        const currentFinalStatus = s.student_final_status === false || s.student_final_status === 'false';
        const isTC = s.status && String(s.status).toUpperCase().includes('TC');
        const targetFinalStatus = (currentFinalStatus || isTC) ? false : s.student_final_status;

        const hasFinalStatusChange = s.student_final_status !== targetFinalStatus;

        const isAlreadySynced = (hasSessionData ? (
          s.active_class === targetClass &&
          String(s.active_section || '').toUpperCase() === String(targetSection || '').toUpperCase() &&
          s.active_roll === targetRoll
        ) : !needsDeleteFromD1) && !hasFinalStatusChange;

        // If student_final_status is false (inactive/TC), always mark needsUpdate=true
        // so they appear in "Need Update" list and D1 deletion is always triggered
        const needsUpdate = !isAlreadySynced || (targetFinalStatus === false);

        return {
          ...s,
          hasSessionData,
          needsDeleteFromD1,
          targetClass,
          targetSection,
          targetRoll,
          targetFinalStatus,
          db_active_class: s.active_class,
          db_active_section: s.active_section,
          db_active_roll: s.active_roll,
          father_mobile: mobile,
          student_key: computedKey,
          needsUpdate
        };
      }).filter((s: any) => (s.hasSessionData || s.needsDeleteFromD1) && s.student_final_status !== false);

      updateSectionOptions();
      renderStudentTable();
      showStatus(`Loaded ${studentsData.length} student records for Session ${session}.`);
    } catch (err: any) {
      console.error(err);
      sectionsContainer.innerHTML = `<div style="padding: 40px; text-align: center; color: #ef4444; background: #fff; border-radius: 16px;">Error loading students: ${err.message}</div>`;
      showStatus(`Failed to load students: ${err.message}`, true);
    }
  }

  function renderTableRowsHTML(list: any[], session: string): string {
    return list.map(s => {
      if (s.needsDeleteFromD1) {
        return `
          <tr data-iid="${s.iid}" style="background: #fff5f5;">
            <td><strong>${s.iid}</strong></td>
            <td>${s.student_name_en || 'N/A'}</td>
            <td>${s.father_mobile || 'N/A'}</td>
            <td><span class="key-badge">${String(s.student_key).padStart(6, '0')}</span></td>
            <td colspan="3" style="color: #ef4444; font-weight: 700; font-size: 13px;">⚠️ No ${session} Data — Will remove from D1</td>
            <td>
              <button class="btn-action btn-red btn-save-row" data-iid="${s.iid}">Clear & Delete D1</button>
            </td>
          </tr>
        `;
      }

      if (s.targetFinalStatus === false) {
        return `
          <tr data-iid="${s.iid}" style="background: #fff5f5;">
            <td><strong>${s.iid}</strong></td>
            <td>${s.student_name_en || 'N/A'}</td>
            <td>${s.father_mobile || 'N/A'}</td>
            <td><span class="key-badge">${String(s.student_key).padStart(6, '0')}</span></td>
            <td colspan="3" style="color: #ef4444; font-weight: 700; font-size: 13px;">⚠️ Inactive (student_final_status=false) — Will remove from D1</td>
            <td>
              <button class="btn-action btn-red btn-save-row" data-iid="${s.iid}">Save & Delete D1</button>
            </td>
          </tr>
        `;
      }

      const btnClass = s.needsUpdate ? 'btn-orange' : 'btn-green';
      return `
        <tr data-iid="${s.iid}">
          <td><strong>${s.iid}</strong></td>
          <td>${s.student_name_en || 'N/A'}</td>
          <td>${s.father_mobile || 'N/A'}</td>
          <td><span class="key-badge">${String(s.student_key).padStart(6, '0')}</span></td>
          <td><span class="val-text">${s.targetClass}</span></td>
          <td><span class="val-text">${s.targetSection}</span></td>
          <td><span class="val-text">${s.targetRoll}</span></td>
          <td>
            <button class="btn-action ${btnClass} btn-save-row" data-iid="${s.iid}">Save & Sync</button>
          </td>
        </tr>
      `;
    }).join('');
  }

  function renderStudentTable() {
    const q = searchInput.value.trim().toLowerCase();
    const cFilter = classSel.value;
    const sFilter = secSel.value;
    const session = sessionSel.value || '2026';

    const filtered = studentsData.filter(s => {
      const clsVal = s.targetClass || s.db_active_class;
      const secVal = s.targetSection || s.db_active_section;
      if (cFilter && String(clsVal) !== cFilter) return false;
      if (sFilter && String(secVal).toUpperCase() !== sFilter.toUpperCase()) return false;
      if (!q) return true;
      return (
        String(s.iid).includes(q) ||
        String(s.student_name_en || '').toLowerCase().includes(q) ||
        String(s.father_mobile || '').includes(q) ||
        String(s.student_key || '').includes(q)
      );
    });

    if (filtered.length === 0) {
      sectionsContainer.innerHTML = '<div style="padding: 40px; text-align: center; color: #64748b; background: #fff; border-radius: 16px;">No matching student records found.</div>';
      return;
    }

    const needUpdateList = filtered.filter(s => s.needsUpdate);
    const updatedList = filtered.filter(s => !s.needsUpdate);

    let html = '';

    // 1. Need Update Section
    html += `
      <div class="sec-card">
        <div class="sec-header sec-header-orange">
          <h3>Need Update (${needUpdateList.length})</h3>
          ${needUpdateList.length > 0 ? `<button class="btn-action btn-orange" id="syncNeedUpdateBtn">⚡ Sync ${needUpdateList.length} Discrepancies</button>` : ''}
        </div>
        ${needUpdateList.length > 0 ? `
          <table class="slog-table">
            <thead>
              <tr>
                <th>IID</th>
                <th>Student Name</th>
                <th>Mobile Number</th>
                <th>Student Key (Last 6 Digits)</th>
                <th>Class (${session})</th>
                <th>Section (${session})</th>
                <th>Roll (${session})</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              ${renderTableRowsHTML(needUpdateList, session)}
            </tbody>
          </table>
        ` : `<div style="padding: 20px; text-align: center; color: #10b981; font-weight: 600;">✅ All records are up to date!</div>`}
      </div>
    `;

    // 2. Updated Section
    html += `
      <div class="sec-card">
        <div class="sec-header sec-header-green">
          <h3>Updated (${updatedList.length})</h3>
        </div>
        ${updatedList.length > 0 ? `
          <table class="slog-table">
            <thead>
              <tr>
                <th>IID</th>
                <th>Student Name</th>
                <th>Mobile Number</th>
                <th>Student Key (Last 6 Digits)</th>
                <th>Class (${session})</th>
                <th>Section (${session})</th>
                <th>Roll (${session})</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              ${renderTableRowsHTML(updatedList, session)}
            </tbody>
          </table>
        ` : `<div style="padding: 20px; text-align: center; color: #64748b;">No updated records yet.</div>`}
      </div>
    `;

    sectionsContainer.innerHTML = html;

    // Attach event listeners for all rows
    filtered.forEach(s => {
      const tr = sectionsContainer.querySelector(`tr[data-iid="${s.iid}"]`);
      if (!tr) return;

      const btn = tr.querySelector('.btn-save-row') as HTMLButtonElement;

      btn.addEventListener('click', async () => {
        await saveAndSyncSingle(String(s.iid), btn);
      });
    });

    // Attach listener to "Sync Discrepancies" button in Need Update section header
    const syncNeedUpdateBtn = document.getElementById('syncNeedUpdateBtn') as HTMLButtonElement;
    if (syncNeedUpdateBtn) {
      syncNeedUpdateBtn.onclick = async () => {
        await bulkSyncList(needUpdateList, syncNeedUpdateBtn, `sync ${needUpdateList.length} Need Update records`);
      };
    }
  }

  async function saveAndSyncSingle(iidStr: string, btn: HTMLButtonElement) {
    const iid = parseInt(iidStr, 10);
    const student = studentsData.find(s => s.iid === iid);
    if (!student) return;



    const session = sessionSel.value || '2026';

    btn.disabled = true;

    showModalProgress("Syncing Student Record", `Updating IID ${iid}...`, 50);

    try {
      await ensureStudentLoginD1Table();

      if (student.needsDeleteFromD1) {
        showModalProgress("Syncing Student Record", `Clearing IID ${iid} from D1...`, 40);
        const deleteD1Sql = `DELETE FROM student_login WHERE iid = ?;`;
        await executeD1Query(deleteD1Sql, [iid]);

        showModalProgress("Syncing Student Record", `Clearing IID ${iid} from Supabase...`, 80);
        const { error: supaErr } = await sb
          .from('student_database')
          .update({ active_class: null, active_section: null, active_roll: null, student_final_status: false })
          .eq('iid', iid);

        if (supaErr) throw new Error(`Supabase error: ${supaErr.message}`);

        // Remove from local list so it disappears without reload
        studentsData = studentsData.filter(s => s.iid !== iid);

        showStatus(`Cleared IID ${iid} from Cloudflare D1 & Supabase active status.`);
      } else if (student.targetFinalStatus === false) {
        showModalProgress("Syncing Student Record", `Removing IID ${iid} from D1 (Inactive)...`, 40);
        const deleteD1Sql = `DELETE FROM student_login WHERE iid = ?;`;
        await executeD1Query(deleteD1Sql, [iid]);

        showModalProgress("Syncing Student Record", `Updating IID ${iid} in Supabase...`, 80);
        const active_class = student.targetClass;
        const active_section = student.targetSection;
        const active_roll = student.targetRoll;
        const student_key = student.student_key;
        
        const supaUpdates: Record<string, any> = {
          active_class,
          active_section,
          active_roll,
          student_key: String(student_key),
          [`class_${session}`]: active_class,
          [`section_${session}`]: active_section,
          [`roll_${session}`]: active_roll,
          student_final_status: false
        };

        const { error: supaErr } = await sb
          .from('student_database')
          .update(supaUpdates)
          .eq('iid', iid);

        if (supaErr) throw new Error(`Supabase error: ${supaErr.message}`);

        // Remove from local list so it disappears without reload
        studentsData = studentsData.filter(s => s.iid !== iid);

        showStatus(`Updated Supabase & removed IID ${iid} from D1 (Inactive).`);
      } else {
        const active_class = student.targetClass;
        const active_section = student.targetSection;
        const active_roll = student.targetRoll;
        const student_key = student.student_key;

        showModalProgress("Syncing Student Record", `Syncing IID ${iid} to D1...`, 40);
        const d1Sql = `INSERT OR REPLACE INTO student_login (iid, active_class, active_section, active_roll, student_key)
          VALUES (?, ?, ?, ?, ?);`;

        await executeD1Query(d1Sql, [iid, active_class, active_section, active_roll, student_key]);

        showModalProgress("Syncing Student Record", `Syncing IID ${iid} to Supabase...`, 80);
        const supaUpdates: Record<string, any> = {
          active_class,
          active_section,
          active_roll,
          student_key: String(student_key),
          [`class_${session}`]: active_class,
          [`section_${session}`]: active_section,
          [`roll_${session}`]: active_roll
        };

        const { error: supaErr } = await sb
          .from('student_database')
          .update(supaUpdates)
          .eq('iid', iid);

        if (supaErr) throw new Error(`Supabase error: ${supaErr.message}`);

        student.db_active_class = active_class;
        student.db_active_section = active_section;
        student.db_active_roll = active_roll;
        student.needsUpdate = false;

        showStatus(`Successfully updated D1 first & Supabase for IID ${iid}.`);
      }

      showModalProgress("Sync Complete", `Finished syncing IID ${iid}`, 100);
      setTimeout(() => {
        hideModalProgress();
        renderStudentTable();
      }, 500);

    } catch (err: any) {
      console.error(err);
      hideModalProgress();
      btn.disabled = false;
      btn.textContent = 'Save & Sync';
      showStatus(`Sync failed for IID ${iid}: ${err.message}`, true);
    }
  }

  async function bulkSyncList(listToSync: any[], btn: HTMLButtonElement, labelText: string) {
    const pendingList = listToSync.filter(s => s.needsUpdate);

    if (pendingList.length === 0) {
      showStatus(`ℹ️ All selected records are already updated & in sync. Write skipped!`);
      alert('All selected records are already updated. No write operations needed.');
      return;
    }

    if (!confirm(`Are you sure you want to ${labelText}? (${pendingList.length} pending updates)`)) return;

    btn.disabled = true;
    btn.textContent = 'Syncing D1...';

    showModalProgress("Bulk Sync in Progress", `Synced D1 first: 0 / ${pendingList.length} records...`, 0);

    const session = sessionSel.value || '2026';

    try {
      await ensureStudentLoginD1Table();

      const chunkSize = 50;
      let syncedCount = 0;

      for (let i = 0; i < pendingList.length; i += chunkSize) {
        const chunk = pendingList.slice(i, i + chunkSize);
        
        for (const student of chunk) {
          if (student.needsDeleteFromD1) {
            const deleteD1Sql = `DELETE FROM student_login WHERE iid = ?;`;
            await executeD1Query(deleteD1Sql, [student.iid]);

            await sb.from('student_database').update({
              active_class: null,
              active_section: null,
              active_roll: null,
              student_final_status: false
            }).eq('iid', student.iid);

            student.db_active_class = null;
            student.db_active_section = null;
            student.db_active_roll = null;
            student.student_final_status = false;
            student.needsDeleteFromD1 = false;
            student.needsUpdate = false;
            // Flag for post-loop removal
            (student as any)._removeAfterSync = true;
          } else if (student.targetFinalStatus === false) {
            const deleteD1Sql = `DELETE FROM student_login WHERE iid = ?;`;
            await executeD1Query(deleteD1Sql, [student.iid]);

            await sb.from('student_database').update({
              active_class: student.targetClass,
              active_section: student.targetSection,
              active_roll: student.targetRoll,
              student_key: String(student.student_key),
              [`class_${session}`]: student.targetClass,
              [`section_${session}`]: student.targetSection,
              [`roll_${session}`]: student.targetRoll,
              student_final_status: false
            }).eq('iid', student.iid);

            student.needsUpdate = false;
            // Flag for post-loop removal
            (student as any)._removeAfterSync = true;
          } else {
            const d1Sql = `INSERT OR REPLACE INTO student_login (iid, active_class, active_section, active_roll, student_key)
              VALUES (?, ?, ?, ?, ?);`;
            
            await executeD1Query(d1Sql, [
              student.iid,
              student.targetClass,
              student.targetSection,
              student.targetRoll,
              student.student_key
            ]);

            await sb.from('student_database').update({
              active_class: student.targetClass,
              active_section: student.targetSection,
              active_roll: student.targetRoll,
              student_key: String(student.student_key),
              [`class_${session}`]: student.targetClass,
              [`section_${session}`]: student.targetSection,
              [`roll_${session}`]: student.targetRoll
            }).eq('iid', student.iid);

            student.db_active_class = student.targetClass;
            student.db_active_section = student.targetSection;
            student.db_active_roll = student.targetRoll;
            student.needsUpdate = false;
          }

          syncedCount++;

          const percent = Math.round((syncedCount / pendingList.length) * 100);
          showModalProgress(
            "Bulk Sync in Progress",
            `Synced D1 first: ${syncedCount} / ${pendingList.length} records...`,
            percent
          );
        }
      }

      // Remove all synced inactive students from local list
      studentsData = studentsData.filter(s => !(s as any)._removeAfterSync);

      showModalProgress("Sync Complete 🎉", `Finished processing all ${syncedCount} records!`, 100);
      
      setTimeout(() => {
        hideModalProgress();
        renderStudentTable();
        showStatus(`🎉 Bulk D1 First Sync Complete! ${syncedCount} pending records updated.`);
        alert(`Successfully synced ${syncedCount} pending student records.`);
      }, 600);

    } catch (err: any) {
      console.error(err);
      hideModalProgress();
      showStatus(`Bulk Sync Failed: ${err.message}`, true);
      alert(`Bulk Sync Failed: ${err.message}`);
    } finally {
      btn.disabled = false;
      btn.textContent = '⚡ Bulk Sync';
    }
  }

  syncD1AllBtn.onclick = async () => {
    const q = searchInput.value.trim().toLowerCase();
    const cFilter = classSel.value;
    const sFilter = secSel.value;

    const listToSync = studentsData.filter(s => {
      const clsVal = s.targetClass || s.db_active_class;
      const secVal = s.targetSection || s.db_active_section;
      if (cFilter && String(clsVal) !== cFilter) return false;
      if (sFilter && String(secVal).toUpperCase() !== sFilter.toUpperCase()) return false;
      if (!q) return true;
      return (
        String(s.iid).includes(q) ||
        String(s.student_name_en || '').toLowerCase().includes(q) ||
        String(s.father_mobile || '').includes(q)
      );
    });

    await bulkSyncList(listToSync, syncD1AllBtn, `sync all filtered records to D1 first`);
  };

  function updateSectionOptions() {
    const currentSecVal = secSel.value;
    const cFilter = classSel.value;

    const sectionsSet = new Set<string>();
    studentsData.forEach(s => {
      if (!cFilter || String(s.targetClass) === cFilter || String(s.db_active_class) === cFilter) {
        if (s.targetSection) {
          sectionsSet.add(String(s.targetSection).trim().toUpperCase());
        }
        if (s.db_active_section) {
          sectionsSet.add(String(s.db_active_section).trim().toUpperCase());
        }
      }
    });

    const sortedSections = Array.from(sectionsSet).sort();

    let optionsHtml = '<option value="">All Sections</option>';
    sortedSections.forEach(sec => {
      const isSelected = sec === currentSecVal ? 'selected' : '';
      optionsHtml += `<option value="${sec}" ${isSelected}>Section ${sec}</option>`;
    });

    secSel.innerHTML = optionsHtml;
  }

  loadDataBtn.onclick = loadStudents;
  sessionSel.onchange = loadStudents;
  searchInput.oninput = renderStudentTable;
  classSel.onchange = () => {
    updateSectionOptions();
    renderStudentTable();
  };
  secSel.onchange = renderStudentTable;

  // Auto load on open
  await loadStudents();
}
